import React, {useMemo} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../components/content-box/ContentBox";
import Dependencies from "../../../../../utilities/Dependencies";
import Environment from "../../../../../services/interfaces/Environment";
import PageActions from "../../../../base/PageActions";
import '../../../../styles/Common.scss';
import '../WorldPages.scss';
import '../WorldDynamicContent.scss';
import Breadcrumbs from "../../../../../components/breadcrumbs/Breadcrumbs";

interface GeographyPageProps {
    dependencies: Dependencies,
    env: Environment
}

const GeographyPage = (props: GeographyPageProps) => {

    const page: PageActions = useMemo(() => new PageActions({
        dependencies: props.dependencies,
        env: props.env,
        title: `Casarok Geography - Chase the Fox`,
        description: "The world of Casarok has a unique geography, separated between the eras of a Pangaea-like planet and a more continental one!",
        keywords: ["casarok", "casarok geography", "fantasy world", "fantasy geography", "pangaea world", "continental world"],
        imgSrc: `chase/world/geography/post-cataclysm.png`,
        url: `world/geography`
    }), [props]);

    const switchVisible = (): void => {
        let initiallyVisibleTarget: HTMLElement = document.getElementById('visible-element')!;
        let initiallyHiddenTarget: HTMLElement = document.getElementById('hidden-element')!;

        if (initiallyVisibleTarget.className.includes('visible')) {
            initiallyVisibleTarget.classList.add('switch-hidden');
            initiallyVisibleTarget.classList.remove('switch-visible');
            initiallyHiddenTarget.classList.add('switch-visible');
            initiallyHiddenTarget.classList.remove('switch-hidden');
        } else {
            initiallyHiddenTarget.classList.add('switch-hidden');
            initiallyHiddenTarget.classList.remove('switch-visible');
            initiallyVisibleTarget.classList.add('switch-visible');
            initiallyVisibleTarget.classList.remove('switch-hidden');
        }
    }

    return page.generateMetaContent(
        <>
            <ContentBox header="The Geography of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <div className="world-content center">
                        <button className="world-button" onClick={() => switchVisible()}>
                            Show the map of the other era
                        </button>
                    </div>
                    <img id="hidden-element"
                         className="width-50-percent switch-hidden"
                         src={page.image().getCdnImageSrc(`chase/world/geography/post-cataclysm.png`)}
                         alt="Map of Modern Casarok"
                         title="Map of Modern Casarok"/>
                    <img id="visible-element"
                         className="block width-50-percent switch-visible"
                         src={page.image().getCdnImageSrc(`chase/world/geography/pre-cataclysm.png`)}
                         alt="Map of Historical Casarok"
                         title="Map of Historical Casarok"/>
                    <p>
                        The planet is split fairly evenly between land and water at about 50% of the surface area for
                        each. Practically all biomes exist on land from plains and forests to deserts and tundra. There
                        are also wastelands on the land, areas of Casarok where few plants, ferals, or even anthros can
                        survive. Underwater creatures live in separate biomes. For example there are sandbars, coral
                        reefs, kelp forests, continental shelves, submarine volcanoes, and the extreme depths. Certain
                        types of creature thrive in each type of biome.
                    </p>
                    <p>
                        The map of the world is split between pre-Cataclysm and post-Cataclysm. Before 
                        the <NavLink className="world-link" to="/world/history">Cataclysmic Age</NavLink>, there 
                        was only one landmass. The land formed one large Pangaea-like continent. After the Great 
                        Cataclysm struck, the landmass was split into the six continents: (from north to south) the 
                        Snowscape, Sequia, Centralia, Euraline, Phaerolis, and the Frostland. The map above can be 
                        swapped, and major landmarks in the world are listed on the opposite page.
                    </p>
                    <h3 className="headline">Landmarks</h3>
                    <ul className="spaced">
                        <li>
                            <strong>Origin Mountain</strong>: At the very center of the continent, it is the largest
                                    peak in the world. It marks 0-degrees latitude and 0-degrees longitude for all
                                    Casarokan cartographers. The mountain is home to some of Casarok's most
                                    dangerous creatures but is said to hold great secrets about the world.
                        </li>
                        <li>
                            <strong>Eldwood Forest</strong>: The thick forest that surrounds Origin Mountain. Many
                                    dangerous creatures lie within, especially many
                                    hostile <NavLink className="world-link" to="/world/bestiary">Floradons</NavLink>. A
                                    powerful clan of owlbeast Aspects live here as well, guarding the forest from
                                    intruders.
                        </li>
                        <li>
                            <strong>The Snowscape</strong>: The arctic tundra that covers all of northern Casarok.
                                    After the Great Cataclysm, it split off into its own continent. All creatures in
                                    this area of the world are adapted to the extreme cold that occurs during the
                                    winter months.
                        </li>
                        <li>
                            <strong>King's Crown</strong>: The area of the Snowscape that has formed glaciers and
                                    fjords at the very northernmost point of the world. The very tip on the center
                                    peninsula is known as King's Crown Point. Normally this location would be too
                                    hostile to be important; however, it is a source of
                                    powerful <NavLink className="world-link" to="/world/magic/elements/frost">frost magic</NavLink> and
                                    important resources such as oil.
                        </li>
                        <li>
                            <strong>The Frostland</strong>: The antarctic tundra of southern Casarok. It became its
                                    own continent after the Great Cataclysm as well. A clan of frost dragons are
                                    said to live here that watch over the Great Library. The whereabouts of this
                                    landmark are unknown, but it is said to contain all the history of Casarok that
                                    was lost from the Pre-Cataclysmic Era.
                        </li>
                        <li>
                            <strong>Breior Mountains</strong>: The longest mountain range in all of Casarok. It
                                    divides the Snowscape from the central and eastern parts of the world. Until
                                    modern times, it was very treacherous crossing over these mountains due to the
                                    sheer height and powerful creatures that inhabited this area.
                        </li>
                        <li>
                            <strong>Sha Zur Mountains</strong>: A smaller mountain range that extends south from the
                                    Breior mountains. It acts as a natural divider between central and eastern
                                    Casarok.
                        </li>
                        <li>
                            <strong>The Firelands</strong>: A small wasteland area nestled in the corner between the
                                    Breior and Sha Zur Mountains. Very few creatures live here, but some
                                    some <NavLink className="world-link" to="/world/bestiary/draconic/dracamma">dracammas</NavLink> and
                                    salamander Aspects can live in this area for example.
                        </li>
                        <li>
                            <strong>Mount Coreptus</strong>: A large volcano that marks an area of high volcanic
                                    activity at the western end of the Breior mountains. It is said to have been a
                                    major catalyst in the Great Cataclysm. The area around this mountain is home to
                                    a strong clan of dracammas.
                        </li>
                        <li>
                            <strong>Acaraho Mountains</strong>: A small mountain range in western Casarok. A lush
                                    rainforest sits between the mountains and the ocean that is known to be some of
                                    the best hunting grounds on land.
                        </li>
                        <li>
                            <strong>Cat's Head Island</strong>: A large island that sits in the cape of southwestern
                                    Casarok. It is a mostly peaceful area, and many anthros call this place home.
                        </li>
                        <li>
                            <strong>Escanina Islands</strong>: A small chain of tropical islands in central eastern
                                    Casarok. The largest of these islands is known as Crescent Island. The name was
                                    given to these islands because of the large canid population that lives there.
                        </li>
                        <li>
                            <strong>The Sandsprawl</strong>: The largest desert in Casarok, located in the
                                    southeastern portion of the world. After the Great Cataclysm, it spread out and
                                    became an even larger part of Phaerolis. Despite the harsh environment, it gave
                                    way to some of the strongest empires in the world.
                        </li>
                        <li>
                            <strong>The Deadly Steppes</strong>: The wasteland area near or surrounded by the
                                    Sandsprawl, depending on the time in history. It is said to be one of the
                                    deadliest areas in all of Casarok. Extremely few creatures can survive the harsh
                                    environment.
                       </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default GeographyPage;